import React, { useState } from 'react';
import { Button } from 'react-bootstrap';

const PaidInternshipPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    university: '',
    degree: '',
    year: '',
    preferredProgram: '',
    experience: '',
    motivation: '',
    portfolio: '',
    availability: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');

  const internshipPrograms = [
    {
      id: 1,
      title: "Quantum Computing Developer",
      icon: "⚛️",
      category: "Quantum Technologies",
      description: "Work on cutting-edge quantum algorithms, quantum machine learning, and quantum cryptography projects with global research teams.",
      duration: "6 months",
      stipend: "₹25,000",
      skills: ["Python", "Qiskit", "Cirq", "Quantum Algorithms", "Linear Algebra"],
      futureScope: "High demand by 2028 with quantum supremacy applications across industries",
      projects: ["Quantum ML Models", "Quantum Cryptography Systems", "Quantum Optimization Algorithms"]
    },
    {
      id: 2,
      title: "AI/LLM Research Scientist",
      icon: "🧠",
      category: "Artificial Intelligence",
      description: "Develop advanced AI models, work on LLM fine-tuning, and create intelligent applications for real-world problems.",
      duration: "6 months",
      stipend: "₹25,000",
      skills: ["PyTorch", "Transformers", "NLP", "Computer Vision", "MLOps"],
      futureScope: "Critical role in AGI development by 2029, with 300% job growth expected",
      projects: ["Custom LLM Development", "Multimodal AI Systems", "AI Safety Research"]
    },
    {
      id: 3,
      title: "Space Technology Engineer",
      icon: "🛰️",
      category: "Space & Aerospace",
      description: "Design satellite communication systems, space mission planning, and work on next-gen orbital mechanics.",
      duration: "6 months",
      stipend: "₹25,000",
      skills: ["MATLAB", "Python", "Satellite Systems", "RF Engineering", "Orbital Mechanics"],
      futureScope: "Exponential growth with space commercialization reaching $1T by 2030",
      projects: ["CubeSat Development", "Inter-planetary Communication", "Space Debris Tracking Systems"]
    },
    {
      id: 4,
      title: "Blockchain & Web3 Developer",
      icon: "⛓️",
      category: "Blockchain & DeFi",
      description: "Build DeFi applications, smart contracts, and decentralized systems for agriculture and financial sectors.",
      duration: "6 months",
      stipend: "₹25,000",
      skills: ["Solidity", "Web3.js", "React", "IPFS", "DeFi Protocols"],
      futureScope: "Web3 revolution driving 90% enterprise adoption by 2028",
      projects: ["AgriChain Platform", "DeFi Lending Protocol", "Cross-chain NFT Marketplace"]
    },
    {
      id: 5,
      title: "AgriTech Innovation Specialist",
      icon: "🌱",
      category: "Agriculture Technology",
      description: "Develop precision agriculture solutions, IoT sensors, and AI-driven crop monitoring systems.",
      duration: "6 months",
      stipend: "₹25,000",
      skills: ["IoT", "Machine Learning", "Computer Vision", "Sensors", "Data Analytics"],
      futureScope: "Essential for feeding 10 billion people by 2050, $100B market opportunity",
      projects: ["Smart Irrigation Systems", "Crop Disease Detection AI", "Yield Prediction Models"]
    },
    {
      id: 6,
      title: "Renewable Energy Systems Developer",
      icon: "⚡",
      category: "Clean Energy",
      description: "Design smart grid systems, energy storage solutions, and renewable energy optimization algorithms.",
      duration: "6 months",
      stipend: "₹25,000",
      skills: ["MATLAB", "Python", "Power Systems", "Battery Tech", "Grid Analytics"],
      futureScope: "100% renewable transition by 2035 creates $50T global market",
      projects: ["Smart Grid Optimization", "Battery Management Systems", "Solar Forecasting AI"]
    },
    {
      id: 7,
      title: "AR/VR/Metaverse Developer",
      icon: "🥽",
      category: "Extended Reality",
      description: "Create immersive experiences, metaverse applications, and next-generation user interfaces.",
      duration: "6 months",
      stipend: "₹25,000",
      skills: ["Unity", "Unreal Engine", "C#", "3D Modeling", "WebXR"],
      futureScope: "$800B metaverse market by 2030, transforming all industries",
      projects: ["Virtual Training Platforms", "AR Industrial Applications", "Social VR Experiences"]
    },
    {
      id: 8,
      title: "Data Science & Analytics Expert",
      icon: "📊",
      category: "Data Science",
      description: "Build predictive models, perform advanced analytics, and create data-driven solutions for enterprise clients.",
      duration: "6 months",
      stipend: "₹25,000",
      skills: ["Python", "R", "SQL", "Tableau", "Machine Learning", "Statistics"],
      futureScope: "Data is the new oil - exponential demand with 200% job growth through 2030",
      projects: ["Predictive Analytics Platform", "Real-time Dashboards", "AI-driven Business Insights"]
    }
  ];

  const filteredPrograms = internshipPrograms.filter(program =>
    program.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    program.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    program.skills.some(skill => skill.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      alert('Application submitted successfully! Proceeding to payment...');
      setIsSubmitting(false);
      handlePayment();
      
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Error submitting application. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handlePayment = () => {
    const amount = paymentMethod === 'international' ? '$24 USD' : '₹2,000 INR';
    alert(`Payment gateway would process ${amount}. Integration with Razorpay/Stripe required.`);
    setShowApplicationForm(false);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const interviewStages = [
    { stage: 1, title: "Technical Screening", desc: "Basic technical assessment & coding skills", icon: "💻" },
    { stage: 2, title: "Project Discussion", desc: "Portfolio review & project walkthrough", icon: "💡" },
    { stage: 3, title: "Industry Expert Round", desc: "Domain-specific technical evaluation", icon: "👨‍💼" },
    { stage: 4, title: "CTO Technical Round", desc: "Advanced technical concepts & system design", icon: "🧠" },
    { stage: 5, title: "CEO Leadership Round", desc: "Vision, leadership skills & cultural fit", icon: "🏆" },
    { stage: 6, title: "Final Selection", desc: "Offer discussion & project assignment", icon: "🤝" }
  ];

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #ffffffff 0%, #ffffffff 100%)',
      fontFamily: 'Arial, sans-serif'
    },
    
    hero: {
      backgroundColor: 'rgba(0, 0, 0, 1)',
      color: 'white',
      padding: '4rem 1rem',
      textAlign: 'center'
    },
    heroContainer: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    heroTitle: {
      fontSize: 'clamp(2rem, 5vw, 3rem)',
      fontWeight: 'bold',
      marginBottom: '1rem',
      background: 'linear-gradient(45deg, #00f2fe, #4facfe)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    heroSubtitle: {
      fontSize: 'clamp(1rem, 3vw, 1.2rem)',
      marginBottom: '2rem',
      opacity: 0.9
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
      marginTop: '3rem'
    },
    statCard: {
      backgroundColor: 'rgba(255, 255, 255, 0.87)',
      backdropFilter: 'blur(1px)',
      padding: '2rem',
      borderRadius: '15px',
      textAlign: 'center',
      color:'#000000ff',
  
     
    },
    mainContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '3rem 1rem'
    },
    whyChooseSection: {
      background: 'linear-gradient(135deg, #667eea, #764ba2)',
      borderRadius: '20px',
      padding: '3rem',
      marginBottom: '3rem',
      color: 'white'
    },
    benefitsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '1rem'
    },
    benefit: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem'
    },
    searchContainer: {
      maxWidth: '500px',
      margin: '0 auto 3rem auto'
    },
    searchWrapper: {
      position: 'relative'
    },
    searchIcon: {
      position: 'absolute',
      left: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#666'
    },
    searchInput: {
      width: '100%',
      padding: '1rem 1rem 1rem 3rem',
      borderRadius: '50px',
      border: 'none',
      backgroundColor: 'white',
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      fontSize: '1rem'
    },
    programsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '2rem',
      marginBottom: '3rem'
    },
    programCard: {
      backgroundColor: 'white',
      borderRadius: '20px',
      padding: '2rem',
      boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      border: '1px solid #f0f0f0'
    },
    interviewSection: {
      backgroundColor: 'white',
      borderRadius: '20px',
      padding: '3rem',
      marginBottom: '3rem',
      boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
    },
    interviewGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '2rem'
    },
    stageIcon: {
      width: '80px',
      height: '80px',
      background: 'linear-gradient(135deg, #667eea, #764ba2)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1rem auto',
      fontSize: '2rem'
    },
    footer: {
      backgroundColor: 'white',
      borderRadius: '20px',
      padding: '3rem',
      boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
    },
    footerGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '2rem'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '20px',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflow: 'auto'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem'
    },
    form: {
      display: 'grid',
      gap: '1.5rem'
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem'
    },
    input: {
      width: '100%',
      padding: '0.8rem',
      border: '2px solid #e0e0e0',
      borderRadius: '10px',
      fontSize: '1rem'
    },
    textarea: {
      width: '100%',
      padding: '0.8rem',
      border: '2px solid #e0e0e0',
      borderRadius: '10px',
      fontSize: '1rem',
      minHeight: '100px',
      resize: 'vertical'
    },
    select: {
      width: '100%',
      padding: '0.8rem',
      border: '2px solid #e0e0e0',
      borderRadius: '10px',
      fontSize: '1rem'
    },
    button: {
      width: '100%',
      padding: '1rem',
      background: 'linear-gradient(135deg, #667eea, #764ba2)',
      color: 'white',
      border: 'none',
      borderRadius: '15px',
      fontSize: '1rem',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    paymentSection: {
      backgroundColor: '#f8f9ff',
      padding: '1.5rem',
      borderRadius: '15px',
      marginBottom: '1.5rem'
    }
  };

  // Media queries for responsive design
  const mediaQueries = `
    @media (max-width: 768px) {
     
      .form-row {
        grid-template-columns: 1fr !important;
      }
      .hero-title {
        font-size: 2rem !important;
      }
      .programs-grid {
        grid-template-columns: 1fr !important;
      }
      .stats-grid {
        grid-template-columns: repeat(2, 1fr) !important;
      }
      .interview-grid {
        grid-template-columns: 1fr !important;
      }
      .benefits-grid {
        grid-template-columns: 1fr !important;
      }
      .footer-grid {
        grid-template-columns: 1fr !important;
      }
    }
    
    @media (max-width: 480px) {
      .stats-grid {
        grid-template-columns: 1fr !important;
      }
      .modal-content {
        margin: 0.5rem;
      }
    }
  `;

  return (
    <>
      <style>{mediaQueries}</style>
      <div style={styles.container}>
      

        {/* Hero Section */}
        <div style={styles.hero}>
          <div style={styles.heroContainer}>
            <h1 style={styles.heroTitle} className="hero-title my-3 py-5">
              Paid Internship Programs
            </h1>
                    <Button className='bg-success btn-lg  me-2 rounded-5 border-1 py-2 px-5 mb-3' > <a className='text-white fw-bold' href="/company-internships"> Company Internships</a></Button>
              <Button className='bg-primary  me-2  btn-lg rounded-5 border-1 py-2 px-5 mb-3 ' > <a className='text-white' href="/enroll">Enroll For Training</a></Button>
            <p>Work on real-world, company-aligned projects that prepare you for industry success.</p>
            <p style={styles.heroSubtitle}>
              Real-World Projects | Global Exposure | ₹25,000 - ₹1,00,000 Stipend | International Opportunities
            </p>
            
            {/* Stats Cards */}
            <div style={styles.statsGrid} className="stats-grid">
              {[
                { icon: "🌍", title: "Global Projects", desc: "International exposure" },
                { icon: "💰", title: "₹25,000 Stipend", desc: "Performance based" },
                { icon: "👥", title: "Expert Mentors", desc: "Industry professionals" },
                { icon: "🏅", title: "Certification", desc: "Industry recognized" }
              ].map((stat, idx) => (
                <div key={idx} style={styles.statCard}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{stat.icon}</div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{stat.title}</h3>
                  <p style={{ fontSize: '0.9rem', opacity: 0.8 }}>{stat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={styles.mainContent}>
          
          {/* Why Choose Section */}
          <div style={styles.whyChooseSection}>
            <h2 style={{ fontSize: '3rem', textAlign: 'center', marginBottom: '2rem', color:'white',fontWeight:'bold' }}>
              🎯 Why Choose Our Internship?
            </h2>
            <div style={styles.benefitsGrid} className="benefits-grid">
              {[
                "Work on real company projects with global impact",
                "6-stage interview process with industry experts", 
                "Direct mentorship from CTO, CEO, and scientists",
                "Future-ready skills for 2028-2030 job market",
                "International collaboration opportunities",
                "Performance-based stipend up to ₹25,000"
              ].map((benefit, idx) => (
                <div key={idx} style={styles.benefit}>
                  <span style={{ color: '#4CAF50', fontSize: '1.2rem' }}>✓</span>
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Search Bar */}
          <div style={styles.searchContainer}>
            <div style={styles.searchWrapper}>
              <span style={styles.searchIcon}>🔍</span>
              <input
                type="text"
                placeholder="Search internship programs..."
                style={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Programs Grid */}
          <div style={styles.programsGrid} className="programs-grid">
            {filteredPrograms.map((program) => (
              <div
                key={program.id}
                style={styles.programCard}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 15px 40px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.1)';
                }}
                onClick={() => setSelectedProgram(program)}
              >
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{program.icon}</div>
                  <h3 style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#333', marginBottom: '0.5rem' }}>
                    {program.title}
                  </h3>
                  <span style={{
                    backgroundColor: '#667eea',
                    color: 'white',
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    fontSize: '0.8rem'
                  }}>
                    {program.category}
                  </span>
                </div>
                
                <p style={{ color: '#666', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                  {program.description}
                </p>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#666' }}>Duration:</span>
                    <span style={{ fontWeight: 'bold' }}>{program.duration}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#666' }}>Max Stipend:</span>
                    <span style={{ color: '#4CAF50', fontWeight: 'bold' }}>{program.stipend}</span>
                  </div>
                </div>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>Key Skills:</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {program.skills.slice(0, 3).map((skill, idx) => (
                      <span key={idx} style={{
                        backgroundColor: '#f0f8ff',
                        color: '#667eea',
                        padding: '0.3rem 0.8rem',
                        borderRadius: '15px',
                        fontSize: '0.8rem',
                        fontWeight: '500'
                      }}>
                        {skill}
                      </span>
                    ))}
                    {program.skills.length > 3 && (
                      <span style={{
                        backgroundColor: '#f5f5f5',
                        color: '#666',
                        padding: '0.3rem 0.8rem',
                        borderRadius: '15px',
                        fontSize: '0.8rem'
                      }}>
                        +{program.skills.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedProgram(program);
                    setShowApplicationForm(true);
                  }}
                  style={styles.button}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #5a67d8, #6b46c1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #667eea, #764ba2)';
                  }}
                >
                  Apply Now - ₹2,000 ($24 USD)
                </button>
              </div>
            ))}
          </div>

          {/* Interview Process Section */}
          <div style={styles.interviewSection}>
            <h2 style={{ fontSize: '2.5rem', textAlign: 'center', marginBottom: '3rem', color: '#333' }}>
              6-Stage Interview Process
            </h2>
            <div style={styles.interviewGrid} className="interview-grid">
              {interviewStages.map((stage) => (
                <div key={stage.stage} style={{ textAlign: 'center' }}>
                  <div style={styles.stageIcon}>
                    {stage.icon}
                  </div>
                  <h4 style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#333' }}>
                    Stage {stage.stage}: {stage.title}
                  </h4>
                  <p style={{ color: '#666', fontSize: '0.9rem' }}>{stage.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Footer Info */}
          <div style={styles.footer}>
            <div style={styles.footerGrid} className="footer-grid">
              <div>
                <h3 style={{ color: '#333', marginBottom: '1rem' }}>📞 Contact Information</h3>
                <div style={{ color: '#666', lineHeight: '2' }}>
                  <div>📧 internships@olewinacademy.com</div>
                  <div>📱 +91 8639449423 </div>
                  <div>📍 Olewin Academy, India</div>
                </div>
              </div>
              
              <div>
                <h3 style={{ color: '#333', marginBottom: '1rem' }}>ℹ️ Important Information</h3>
                <div style={{ color: '#666', fontSize: '0.9rem', lineHeight: '1.8' }}>
                  <div>• Registration fee: ₹2,000 (Non-refundable)</div>
                  <div>• International students: $24 USD</div>
                  <div>• 6-stage interview process</div>
                  <div>• Performance-based stipend up to ₹25,000</div>
                  <div>• 6-month program duration</div>
                </div>
              </div>
              
              <div>
                <h3 style={{ color: '#333', marginBottom: '1rem' }}>🔒 Security & Trust</h3>
                <div style={{ color: '#666', fontSize: '0.9rem', lineHeight: '1.8' }}>
                  <div>✅ Secure payment gateway</div>
                  <div>✅ Data encrypted & protected</div>
                  <div>✅ GDPR compliant</div>
                  <div>✅ Industry-standard security</div>
                </div>
              </div>
            </div>
            
            <div style={{ 
              borderTop: '1px solid #eee', 
              marginTop: '2rem', 
              paddingTop: '2rem', 
              textAlign: 'center' 
            }}>
              <h3 style={{ color: '#333', marginBottom: '1rem' }}>Ready to Shape the Future?</h3>
              <p style={{ color: '#666', marginBottom: '1rem' }}>
                Join thousands of students already building tomorrow's technology today.
              </p>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '2rem', 
                flexWrap: 'wrap',
                fontSize: '0.9rem',
                color: '#666'
              }}>
                <div>⭐ 4.9/5 Student Rating</div>
                <div>👥 5000+ Alumni Network</div>
                <div>🌍 50+ Countries</div>
              </div>
            </div>
          </div>
        </div>

        {/* Application Form Modal */}
        {showApplicationForm && (
          <div style={styles.modal}>
            <div style={styles.modalContent}>
              <div style={{ padding: '2rem' }}>
                <div style={styles.modalHeader}>
                  <h2 style={{ color: '#333', fontSize: '1.8rem', margin: 0 }}>Apply for Internship</h2>
                  <button
                    onClick={() => setShowApplicationForm(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '2rem',
                      cursor: 'pointer',
                      color: '#666'
                    }}
                  >
                    ×
                  </button>
                </div>

                {selectedProgram && (
                  <div style={{
                    backgroundColor: '#f8f9ff',
                    padding: '1.5rem',
                    borderRadius: '15px',
                    marginBottom: '2rem'
                  }}>
                    <h3 style={{ color: '#667eea', fontWeight: 'bold', margin: 0 }}>{selectedProgram.title}</h3>
                    <p style={{ color: '#666', fontSize: '0.9rem', margin: '0.5rem 0 0 0' }}>
                      {selectedProgram.category}
                    </p>
                  </div>
                )}

                <form onSubmit={handleFormSubmit} style={styles.form}>
                  <div style={styles.formRow} className="form-row">
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        required
                        value={formData.fullName}
                        onChange={handleInputChange}
                        style={styles.input}
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        style={styles.input}
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>

                  <div style={styles.formRow} className="form-row">
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        style={styles.input}
                        placeholder="+91 12345 67890"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                        University/College *
                      </label>
                      <input
                        type="text"
                        name="university"
                        required
                        value={formData.university}
                        onChange={handleInputChange}
                        style={styles.input}
                        placeholder="Enter your institution"
                      />
                    </div>
                  </div>

                  <div style={styles.formRow} className="form-row">
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                        Degree/Field of Study *
                      </label>
                      <input
                        type="text"
                        name="degree"
                        required
                        value={formData.degree}
                        onChange={handleInputChange}
                        style={styles.input}
                        placeholder="e.g., Computer Science"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                        Current Year *
                      </label>
                      <select
                        name="year"
                        required
                        value={formData.year}
                        onChange={handleInputChange}
                        style={styles.select}
                      >
                        <option value="">Select Year</option>
                        <option value="1st Year">1st Year</option>
                        <option value="2nd Year">2nd Year</option>
                        <option value="3rd Year">3rd Year</option>
                        <option value="4th Year">4th Year</option>
                        <option value="Graduate">Graduate</option>
                        <option value="Postgraduate">Postgraduate</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                      Preferred Program *
                    </label>
                    <select
                      name="preferredProgram"
                      required
                      value={formData.preferredProgram || (selectedProgram ? selectedProgram.title : '')}
                      onChange={handleInputChange}
                      style={styles.select}
                    >
                      <option value="">Select Program</option>
                      {internshipPrograms.map((program) => (
                        <option key={program.id} value={program.title}>
                          {program.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                      Relevant Experience
                    </label>
                    <textarea
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      style={styles.textarea}
                      placeholder="Describe your relevant projects, internships, or experience..."
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                      Motivation & Goals *
                    </label>
                    <textarea
                      name="motivation"
                      required
                      value={formData.motivation}
                      onChange={handleInputChange}
                      style={styles.textarea}
                      placeholder="Why do you want to join this internship? What are your career goals?"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                      Portfolio/GitHub/LinkedIn URL
                    </label>
                    <input
                      type="url"
                      name="portfolio"
                      value={formData.portfolio}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="https://github.com/yourusername or portfolio link"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                      Availability *
                    </label>
                    <select
                      name="availability"
                      required
                      value={formData.availability}
                      onChange={handleInputChange}
                      style={styles.select}
                    >
                      <option value="">Select Availability</option>
                      <option value="Immediate">Immediate (Within 1 week)</option>
                      <option value="2 weeks">Within 2 weeks</option>
                      <option value="1 month">Within 1 month</option>
                      <option value="After semester">After current semester</option>
                      <option value="Custom">Custom (specify in motivation)</option>
                    </select>
                  </div>

                  {/* Payment Method Selection */}
                  <div style={styles.paymentSection}>
                    <h3 style={{ color: '#333', marginBottom: '1rem', fontSize: '1.2rem' }}>Payment Method</h3>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="card"
                          checked={paymentMethod === 'card'}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                        />
                        💳 Credit/Debit Card (₹2,000 INR)
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="upi"
                          checked={paymentMethod === 'upi'}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                        />
                        📱 UPI (₹2,000 INR)
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="international"
                          checked={paymentMethod === 'international'}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                        />
                        🌍 International ($24 USD)
                      </label>
                    </div>
                    <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#e8f5e8', borderRadius: '10px' }}>
                      <p style={{ margin: 0, fontSize: '0.9rem', color: '#2e7d32' }}>
                        🔒 Secure payment processing with industry-standard encryption. 
                        Registration fee is non-refundable but guarantees your interview slot.
                      </p>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      ...styles.button,
                      opacity: isSubmitting ? 0.7 : 1,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isSubmitting ? (
                      <span>🔄 Submitting Application...</span>
                    ) : (
                      <span>🚀 Submit Application & Pay {paymentMethod === 'international' ? '$24 USD' : '₹2,000 INR'}</span>
                    )}
                  </button>

                  <div style={{ 
                    textAlign: 'center', 
                    fontSize: '0.8rem', 
                    color: '#666', 
                    marginTop: '1rem',
                    lineHeight: '1.5'
                  }}>
                    By submitting this application, you agree to our Terms of Service and Privacy Policy.
                    <br />
                    You will receive a confirmation email with further instructions after successful payment.
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PaidInternshipPage;